import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { ServerHotbarPayload, ServerHotbarSnapshotAuthority } from '../../systems/player/PlayerManager';
import type { HeavyFieldsSnapshotMessage, InventoryUpdateMessage } from '../../network/ClientMessageTypes';

/**
 * Feed the server-authoritative hotbar to the CAST source (PlayerManager).
 * Sole remaining caller: the EventBus-relayed heavyFieldsSnapshot handler.
 * Plain `hotbarUpdate` messages reach the cast
 * source via the always-live DIRECT_RELAY_TYPES relay →
 * net:message:hotbarUpdate → PlayerManager (relay-twins fix lane 2026-07-09);
 * the old direct push here was removed as the duplicate. PM caches applies to
 * survive the join race, so an early no-op (PM/player not ready) is harmless.
 */
export function pushHotbarSnapshotToCastSource(
  payload: ServerHotbarPayload,
  authority: ServerHotbarSnapshotAuthority,
): void {
  try {
    const es = ServiceRegistry.getInstance().get('EntitySystem');
    es?.getPlayerManager?.()?.applyServerHotbarSnapshot?.(payload, authority);
  } catch { /* PM not ready — the 1 Hz heavy snapshot retries naturally */ }
}

export function readAtomicHotbarAuthority(
  msg: HeavyFieldsSnapshotMessage,
  sessionId: string,
): ServerHotbarSnapshotAuthority | null {
  const hotbarMode = msg.hotbarMode;
  if (
    (hotbarMode !== 'base' && hotbarMode !== 'peaceful' && hotbarMode !== 'race')
    || typeof msg.peaceful !== 'boolean'
    || msg.peaceful !== (hotbarMode === 'peaceful')
    || !Number.isInteger(msg.level)
    || (msg.level ?? 0) < 1
  ) {
    return null;
  }
  return { sessionId, hotbarMode, peaceful: msg.peaceful, level: msg.level! };
}

/** Keep singleton-backed inventory/equipment windows on the same server
 * snapshot as the React player model. The snapshot repeats at 1 Hz, so an
 * early join frame that arrives before GameScene registers InventorySystem is
 * naturally retried without a client-owned cache. */
export function pushInventoryToSystem(payload: InventoryUpdateMessage): void {
  try {
    ServiceRegistry.getInstance()
      .get('InventorySystem', { optional: true })
      ?.applyServerSnapshot?.(payload);
  } catch { /* GameScene not ready yet — periodic heavy snapshot retries */ }
}
